import React, { useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  MapPin, ChevronLeft, ChevronRight, Check, Phone, Mail, MessageCircle,
  BedDouble, Users, Maximize, Plane, Compass, Star, Quote, ArrowRight,
  Sparkles, UtensilsCrossed, Waves, Dumbbell, Building2, ShoppingBag, Wifi, Flame,
  Music, Utensils, Heart, ConciergeBell,
  Images, X,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";
import HomeBookingWidget from "@/components/HomeBookingWidget";
import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";
import { getSpaFeature } from "@/lib/spaFeatures";
import { getRoomPricing, formatPriceLine } from "@/lib/hotelPricing";
import { useFormatPrice, useCurrency } from "@/contexts/CurrencyContext";
import RoomServicePricing from "@/components/RoomServicePricing";
import RoomPriceTag from "@/components/RoomPriceTag";
import RoomRatesStrip from "@/components/RoomRatesStrip";
import { useHotelGalleries, getRoomImagesFromAdmin } from "@/lib/useHotelGalleries";
import { slugifyRoom } from "@/lib/roomRegistry";
import { slugifyActivity } from "@/lib/activitySlug";
import { getAccomTerms, isBivouac } from "@/lib/propertyType";
import {
  useHotelPropertyGalleries, getHeroUrl, getGalleryUrls,
  hasAnyHero, hasAnyGallery, buildAboutTiles,
} from "@/lib/useHotelPropertyGalleries";
import { useHotelCmsOverrides, applyRoomOverrides } from "@/lib/useHotelCmsOverrides";
import SpaWellnessFeature from "@/components/SpaWellnessFeature";
import HowToBookSection from "@/components/HowToBookSection";
import HotelLocationContact from "@/components/HotelLocationContact";
import { BerberDiamondDivider } from "@/components/BerberDivider";
import HeroSlider from "@/components/HeroSlider";
import RoomImageGallery from "@/components/RoomImageGallery";
import RoomAmenitiesStrip from "@/components/RoomAmenitiesStrip";
import VideoSection from "@/components/VideoSection";
import CampImageGallery from "@/components/CampImageGallery";
import DidYouKnowSection from "@/components/DidYouKnowSection";
import LocalTimeWeatherWidget from "@/components/LocalTimeWeatherWidget";
import StickyHotelNav from "@/components/StickyHotelNav";
import ExperienceTestimonial from "@/components/ExperienceTestimonial";
import { pickRoomTestimonial } from "@/lib/roomTestimonials";
import useRoomTestimonials from "@/lib/useRoomTestimonials";
import HotelReviews from "@/components/HotelReviews";
import HotelAboutMap from "@/components/HotelAboutMap";
import HotelConciergeCTA from "@/components/HotelConciergeCTA";
import QuickBookingPill from "@/components/booking/QuickBookingPill";
import { EditableImage } from "@/components/cms/CmsImage";
import CuratedGallerySection from "@/components/CuratedGallerySection";
import HotelBrochureSection from "@/components/HotelBrochureSection";
import BROCHURES from "@/lib/brochureData";
import useCuratedGallery from "@/lib/useCuratedGallery";

// ── Card-thumbnail defaults ──────────────────────────────────────────────
// Curated stable Pexels stills used as fallbacks when a card item has no
// `image` field of its own. Rotated by card index inside each section.
// Each entry can still be overridden per (hotel, section, index) via the
// inline edit-mode CMS — see `EditableImage` slot ids below.
const THUMB_DEFAULTS = {
  services: [
    "https://images.pexels.com/photos/3865711/pexels-photo-3865711.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/941861/pexels-photo-941861.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/271639/pexels-photo-271639.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3201921/pexels-photo-3201921.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3884438/pexels-photo-3884438.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  excursions: [
    "https://images.pexels.com/photos/1739841/pexels-photo-1739841.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/35771005/pexels-photo-35771005.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3889855/pexels-photo-3889855.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2123573/pexels-photo-2123573.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2403017/pexels-photo-2403017.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  destination: [
    "https://images.pexels.com/photos/3889855/pexels-photo-3889855.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3889840/pexels-photo-3889840.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2549018/pexels-photo-2549018.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/2549019/pexels-photo-2549019.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/4254543/pexels-photo-4254543.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  restaurants: [
    "https://images.pexels.com/photos/2347719/pexels-photo-2347719.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/1640772/pexels-photo-1640772.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/3186654/pexels-photo-3186654.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
  events: [
    "https://images.pexels.com/photos/169198/pexels-photo-169198.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/1244627/pexels-photo-1244627.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/1395967/pexels-photo-1395967.jpeg?auto=compress&cs=tinysrgb&w=1400",
    "https://images.pexels.com/photos/787961/pexels-photo-787961.jpeg?auto=compress&cs=tinysrgb&w=1400",
  ],
};
const pickThumb = (section, idx, ownImage) =>
  ownImage || THUMB_DEFAULTS[section][idx % THUMB_DEFAULTS[section].length];

const ICONS = {
  Sparkles, UtensilsCrossed, Waves, Dumbbell, Building2, ShoppingBag, Wifi,
  Flame, Music, Utensils, Heart, ConciergeBell, Compass,
};

// Helper: pick language-specific text from {en,fr,es} object
const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Build a gallery for a room: its own gallery if defined, else the room image + the hotel's images, deduplicated.
const buildRoomGallery = (room, hotel) => {
  if (room.gallery && room.gallery.length) return room.gallery;
  const pool = [room.image, ...(hotel.gallery || []), hotel.image].filter(Boolean);
  return Array.from(new Set(pool));
};

// Lightweight lightbox modal for room galleries.
const RoomLightbox = ({ images, title, onClose, lang = "fr" }) => {
  const [idx, setIdx] = useState(0);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-4 md:px-12"
      data-testid="room-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
        data-testid="room-lightbox-close"
        aria-label={pick({ en: "Close gallery", fr: "Fermer la galerie", es: "Cerrar galería" }, lang)}
      >
        <X className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={prev}
        className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="room-lightbox-prev"
        aria-label={pick({ en: "Previous image", fr: "Image précédente", es: "Imagen anterior" }, lang)}
      >
        <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <button
        onClick={next}
        className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="room-lightbox-next"
        aria-label={pick({ en: "Next image", fr: "Image suivante", es: "Imagen siguiente" }, lang)}
      >
        <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <div className="max-w-6xl w-full">
        <img
          src={images[idx]}
          alt={`${title} ${idx + 1}`}
          className="w-full max-h-[78vh] object-contain"
          data-testid="room-lightbox-image" loading="lazy" decoding="async"/>
        <div className="mt-6 flex items-center justify-between text-[#FDFBF7]/85">
          <p className="font-serif-x text-xl md:text-2xl">{title}</p>
          <p className="text-xs tracking-[0.25em] uppercase tabular-nums">
            {String(idx + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </p>
        </div>
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <button
              key={`thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`shrink-0 w-20 h-14 overflow-hidden border-2 transition-opacity ${
                i === idx ? "border-[#D4A373] opacity-100" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              data-testid={`room-lightbox-thumb-${i}`}
              aria-label={`Go to image ${i + 1}`}
            >
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const Section = ({ overline, title, intro, children, dark = false, id }) => (  <section
    id={id}
    className={`py-20 md:py-28 ${dark ? "bg-[#1A1513] text-[#FDFBF7]" : ""}`}
    data-testid={`section-${id}`}
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
        <div className="md:col-span-6">
          {overline && (
            <p className={`overline ${dark ? "!text-[#D4A373]" : ""}`}>{overline}</p>
          )}
          <h2
            className={`font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] ${
              dark ? "text-[#FDFBF7]" : "text-[#2C2621]"
            }`}
          >
            {title}
          </h2>
        </div>
        {intro && (
          <div className="md:col-span-5 md:col-start-8 flex items-end">
            <p
              className={`text-lg leading-relaxed ${
                dark ? "text-[#FDFBF7]/75" : "text-[#5C5248]"
              }`}
            >
              {intro}
            </p>
          </div>
        )}
      </div>
      {children}
    </div>
  </section>
);

// ── AddThisPropertyButton ─────────────────────────────────────────
// Single-tap CTA on the property page that toggles THIS property in
// the shared booking enquiry. When already added, it shows a
// "✓ Added to enquiry" state so the user knows they don't need to
// click again. Calls `toggleHotelWithFeedback` so the standard toast
// confirmation fires (and removal goes through the same flow).
const AddThisPropertyButton = ({ hotel, lang }) => {
  const { selectedHotelSlugs, toggleHotelWithFeedback } = useBooking();
  const isAdded = selectedHotelSlugs.includes(hotel.slug);
  return (
    <button
      type="button"
      onClick={() => toggleHotelWithFeedback(hotel.slug)}
      className={`inline-flex items-center justify-center gap-2 max-w-full px-4 md:px-6 py-3.5 text-[9px] md:text-[10px] tracking-[0.2em] md:tracking-[0.3em] uppercase border transition-colors text-center leading-snug break-words ${
        isAdded
          ? "bg-[#1A1513] text-[#FDFBF7] border-[#1A1513]"
          : "bg-[#C16542] text-[#FDFBF7] border-[#C16542] hover:bg-[#A35133] hover:border-[#A35133]"
      }`}
      data-testid="hotel-add-to-enquiry"
      data-no-cms-text="true"
    >
      {isAdded ? (
        <>
          <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
          <span className="min-w-0">
            {pick(
              { en: "Added to enquiry", fr: "Ajouté à votre demande", es: "Añadido a su solicitud" },
              lang
            )}
          </span>
        </>
      ) : (
        <>
          <Sparkles className="w-3.5 h-3.5 shrink-0" strokeWidth={1.7} />
          <span className="min-w-0 hidden md:inline">
            {pick(
              {
                en: `Add ${hotel.name} to my enquiry`,
                fr: `Ajouter ${hotel.name} à ma demande`,
                es: `Añadir ${hotel.name} a mi solicitud`,
              },
              lang
            )}
          </span>
          <span className="min-w-0 md:hidden">
            {pick(
              { en: "Add to enquiry", fr: "Ajouter à ma demande", es: "Añadir a mi solicitud" },
              lang
            )}
          </span>
        </>
      )}
    </button>
  );
};

const HotelDetailPage = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const { parse: parsePrice } = useFormatPrice();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const hotel = HOTELS.find((h) => h.slug === slug);
  const others = HOTELS.filter((h) => h.slug !== slug).slice(0, 4);
  const [galleryRoom, setGalleryRoom] = useState(null);
  // Active camp tab for the Haimas section. Only used when the hotel has
  // multiple camps AND at least one room carries a `camp` field. Defaults
  // to null → resolved to the first tab's slug once we know the list.
  const [activeCampSlug, setActiveCampSlug] = useState(null);
  // Active variant index per variantGroup — used when a category (e.g.
  // "Twin Room") has two design variants (Standard · Superior) shown as
  // internal tabs inside the same room card. Keyed by variantGroup slug.
  const [activeVariants, setActiveVariants] = useState({});
  const adminGalleries = useHotelGalleries(hotel?.slug);
  const propertyGalleries = useHotelPropertyGalleries(hotel?.slug);
  const cmsOverrides = useHotelCmsOverrides(hotel?.slug);
  const { items: adminRoomTestimonials } = useRoomTestimonials(hotel?.slug);
  // Curated gallery is loaded here so BOTH the sticky nav (needs to
  // know whether to show the "Gallery" anchor) and the section itself
  // share a single round-trip.
  const curated = useCuratedGallery(hotel?.slug);
  // Rotate testimonials on each fresh mount so returning visitors see
  // fresh voices without any manual work. Pinned inside a `useMemo` so
  // the choice is stable during the lifetime of this page render.
  const testimonialSeed = useMemo(() => Date.now() + Math.floor(Math.random() * 1000), []);

  if (!hotel) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="hotel-not-found">
        <p>Not found.</p>
        <Link to="/hotels" className="text-[#C16542] mt-4 inline-block">
          ← {t("common.back")}
        </Link>
      </div>
    );
  }

  const d = getHotelDetails(hotel.slug);
  // Merge CMS overrides into the static rooms before any usage
  const mergedRooms = applyRoomOverrides(d.rooms || [], cmsOverrides).filter((r) => r._is_active);
  d.rooms = mergedRooms;

  // Bivouac-aware labels — "Rooms" swaps to "Haimas" (Sahrawi tent
  // vocabulary) on the three desert-camp pages so titles, CTAs, stats
  // and the quick anchor all reflect the type of stay on offer.
  const accomTerms = getAccomTerms(hotel.slug, lang);
  const bivouac = isBivouac(hotel.slug);

  // Build 3-4 narrative slides per hotel, sourced from the property's own data —
  // each hotel therefore gets a homepage-style, multi-slide hero that's truly its own.
  const heroSlides = (() => {
    const gallery = (hotel.gallery || []).filter((g) => g && g !== hotel.image);
    const safeGallery = [
      hotel.image,
      gallery[0] || hotel.image,
      gallery[1] || gallery[0] || hotel.image,
      gallery[2] || gallery[1] || hotel.image,
    ];
    const tagline = pick(d.tagline, lang);
    const welcome = pick(d.welcome, lang);
    const firstExc = (d.excursions && d.excursions[0]) || null;
    const firstResto = (d.fb?.restaurants && d.fb.restaurants[0]) || null;
    const hasSpa = d.spa_menu && d.spa_menu.length > 0;

    const slides = [
      // 0) Video slide — cinematic group-level Sahara film, prepended on
      //    every hotel & bivouac. Same Mux MP4 used on the homepage hero;
      //    HeroSlider auto-renders it as a native <video> when it sees a
      //    `stream.mux.com` URL and falls back to `videoPoster` / `image`
      //    on mobile or while the video loads.
      {
        image: safeGallery[0],
        video: "https://stream.mux.com/HlwGpYi2dBcP007P3601vNiRiY9acrPyBB/medium.mp4",
        videoPoster:
          "https://image.mux.com/HlwGpYi2dBcP007P3601vNiRiY9acrPyBB/thumbnail.jpg?width=1920&time=0",
        badge: hotel.location,
        overline: pick({ en: "Welcome to", fr: "Bienvenue à", es: "Bienvenido a" }, lang) + " · " + hotel.location,
        title: hotel.name.split(" ").slice(0, -1).join(" ") || hotel.name,
        titleAccent: hotel.name.split(" ").slice(-1).join(" "),
        sub: tagline || welcome || pick({
          en: "Press play on southern Morocco — your stay begins here.",
          fr: "Lancez le sud marocain — votre séjour commence ici.",
          es: "Pulse play en el sur de Marruecos — su estancia empieza aquí.",
        }, lang),
        ctaTo: `/booking?hotel=${encodeURIComponent(hotel.slug)}`,
        ctaLabel: t("common.book_now"),
        secondaryTo: "/contact",
        secondaryLabel: pick({ en: "Contact us", fr: "Nous contacter", es: "Contáctanos" }, lang),
      },
      // 1) Property essence
      {
        image: safeGallery[0],
        badge: hotel.location,
        overline: pick({ en: "Grup Xaluca", fr: "Grup Xaluca", es: "Grup Xaluca" }, lang) + " · " + hotel.location,
        title: hotel.name.split(" ").slice(0, -1).join(" ") || hotel.name,
        titleAccent: hotel.name.split(" ").slice(-1).join(" "),
        sub: welcome || tagline,
        ctaTo: `/booking?hotel=${encodeURIComponent(hotel.slug)}`,
        ctaLabel: t("common.book_now"),
        secondaryTo: "/contact",
        secondaryLabel: pick({ en: "Contact us", fr: "Nous contacter", es: "Contáctanos" }, lang),
      },
      // 2) Landscape / location
      {
        image: safeGallery[1],
        badge: d.stats?.coords ? pick(d.stats.coords, lang) || d.stats.coords : hotel.location,
        overline: pick({ en: "Where you'll wake up", fr: "Là où vous vous réveillerez", es: "Donde despertará" }, lang),
        title: pick({ en: "A landscape that", fr: "Un paysage qui", es: "Un paisaje que" }, lang),
        titleAccent: pick({ en: "writes you back.", fr: "vous écrit en retour.", es: "le devuelve la palabra." }, lang),
        sub: tagline || pick({
          en: `${hotel.location} — open horizons, slow light, the wider south of Morocco at the doorstep.`,
          fr: `${hotel.location} — horizons ouverts, lumière lente, le grand sud marocain à la porte.`,
          es: `${hotel.location} — horizontes abiertos, luz lenta, el gran sur de Marruecos a la puerta.`,
        }, lang),
        ctaTo: `/booking?hotel=${encodeURIComponent(hotel.slug)}`,
        ctaLabel: t("common.book_now"),
        secondaryTo: "/contact",
        secondaryLabel: pick({ en: "Contact us", fr: "Nous contacter", es: "Contáctanos" }, lang),
      },
      // 3) Signature experience (if any excursions)
      firstExc && {
        image: safeGallery[2],
        badge: pick(firstExc.duration, lang) || pick({ en: "Signature experience", fr: "Expérience signature", es: "Experiencia insignia" }, lang),
        overline: pick({ en: "What you'll remember", fr: "Ce dont vous vous souviendrez", es: "Lo que recordará" }, lang),
        title: pick(firstExc.title, lang).split(" ").slice(0, -1).join(" ") || pick(firstExc.title, lang),
        titleAccent: pick(firstExc.title, lang).split(" ").slice(-1).join(" "),
        sub: pick(firstExc.desc, lang),
        ctaTo: `/booking?hotel=${encodeURIComponent(hotel.slug)}`,
        ctaLabel: t("common.book_now"),
        secondaryTo: "/contact",
        secondaryLabel: pick({ en: "Contact us", fr: "Nous contacter", es: "Contáctanos" }, lang),
      },
      // 4) Table / wellness (whichever exists, prefer wellness for spa hotels)
      ((hasSpa || firstResto) && {
        image: safeGallery[3],
        badge: hasSpa
          ? pick({ en: "Hammam · massages · rituals", fr: "Hammam · massages · rituels", es: "Hammam · masajes · rituales" }, lang)
          : (firstResto?.cuisine ? pick(firstResto.cuisine, lang) : pick({ en: "At the table", fr: "À table", es: "En la mesa" }, lang)),
        overline: hasSpa
          ? pick({ en: "Wellness rituals", fr: "Rituels bien-être", es: "Rituales de bienestar" }, lang)
          : pick({ en: "At the table", fr: "À table", es: "En la mesa" }, lang),
        title: hasSpa
          ? pick({ en: "Slow steam,", fr: "Vapeur lente,", es: "Vapor lento," }, lang)
          : pick({ en: "Slow cooking,", fr: "Cuisine lente,", es: "Cocina lenta," }, lang),
        titleAccent: hasSpa
          ? pick({ en: "argan rituals.", fr: "rituels à l'argan.", es: "rituales de argán." }, lang)
          : pick({ en: "open fire.", fr: "feu ouvert.", es: "fuego abierto." }, lang),
        sub: hasSpa
          ? pick({
              en: "Beldi black-soap scrubs, argan oil massages and traditional hammams composed for the rhythm of your stay.",
              fr: "Gommages au savon noir Beldi, massages à l'huile d'argan et hammams traditionnels composés au rythme de votre séjour.",
              es: "Exfoliaciones con jabón negro Beldi, masajes con aceite de argán y hammams tradicionales al ritmo de su estancia.",
            }, lang)
          : firstResto
          ? (firstResto.name + " — " + (pick({ en: "the address's signature table.", fr: "la table signature de l'adresse.", es: "la mesa insignia de la dirección." }, lang)))
          : pick({ en: "The table of the house — written in Berber, served in copper.", fr: "La table de la maison — écrite en berbère, servie dans le cuivre.", es: "La mesa de la casa — escrita en bereber, servida en cobre." }, lang),
        ctaTo: `/booking?hotel=${encodeURIComponent(hotel.slug)}`,
        ctaLabel: t("common.book_now"),
        secondaryTo: "/contact",
        secondaryLabel: pick({ en: "Contact us", fr: "Nous contacter", es: "Contáctanos" }, lang),
      }),
    ].filter(Boolean);

    return slides;
  })();

  return (
    <div data-testid={`page-hotel-${hotel.slug}`} className="bg-[#FDFBF7] text-[#2C2621] x-watermark-scope overflow-x-clip">
      {/* HERO — multi-slide narrative slider, built from the property's own data (self-sizing) */}
      <section className="relative overflow-hidden" data-testid="hotel-hero">
        <HeroSlider slides={heroSlides} alt={hotel.name} interval={7500} scope={`hotel.${hotel.slug}`} />
        {/* Back button — pinned above the slider */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-24 left-6 md:left-12 z-20 inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[#FDFBF7]/85 hover:text-[#D4A373] bg-[#1A1513]/30 backdrop-blur-sm px-3 py-2"
          data-testid="hotel-back-button"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          {t("common.back")}
        </button>
        {/* Quick anchor — jump to room & service rates */}
        <a
          href="#rooms-services"
          className="absolute top-24 right-6 md:right-12 z-20 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/85 hover:text-[#D4A373] bg-[#1A1513]/30 backdrop-blur-sm px-3.5 py-2 border border-[#FDFBF7]/15 hover:border-[#D4A373]/60 transition-colors"
          data-testid="hero-rates-anchor"
        >
          <span className="w-1.5 h-1.5 bg-[#D4A373] rounded-full" />
          {pick(
            bivouac
              ? { en: "Haimas & rates", fr: "Haimas & tarifs", es: "Haimas y tarifas" }
              : { en: "Rooms & rates", fr: "Chambres & tarifs", es: "Habitaciones y tarifas" },
            lang
          )}
        </a>
        {/* Coordinates band — bottom-right, like the homepage */}
        <div className="absolute bottom-6 right-6 hidden md:flex items-center gap-2 text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase z-10 pointer-events-none" data-testid="hotel-hero-corner-label">
          <span className="w-12 h-px bg-[#FDFBF7]/40" />
          GRUP XALUCA
        </div>
      </section>

      {/* PLAN YOUR STAY — full-width inquiry strip directly under the hero,
          mirroring the layout used on /home and /booking. A compact
          "Add this property" CTA sits just above so visitors can drop
          this address into the enquiry with a single click before
          customising dates, rooms and extras. The widget reads from the
          same BookingContext as every other surface so anything the
          user builds here syncs across the entire site. */}
      <section
        className="bg-[#FDFBF7] border-b border-[#2C2621]/10"
        data-testid="hotel-hero-booking-widget"
        id="plan-your-stay"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-6 md:pt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#5C5248]">
            {pick(
              { en: "Plan your stay", fr: "Planifier votre séjour", es: "Planificar su estancia" },
              lang
            )}
          </p>
          <AddThisPropertyButton hotel={hotel} lang={lang} />
        </div>
        <HomeBookingWidget />
      </section>

      {/* STICKY SECTION NAV — Quick anchors to Rooms · Services · Spa · Food · Nearby.
          Only anchors whose section actually exists on this hotel are rendered.
          Uses IntersectionObserver to highlight the current section. */}
      <StickyHotelNav
        availableIds={[
          d.rooms && d.rooms.length > 0 ? "rooms" : null,
          curated.count > 0 ? "gallery" : null,
          BROCHURES.some((b) => b.hotelSlug === hotel.slug) ? "brochure" : null,
          d.excursions && d.excursions.length > 0 ? "experiences" : null,
          !bivouac && d.services && d.services.length > 0 ? "services" : null,
          !bivouac && d.spa_menu && d.spa_menu.length > 0 ? "spa" : null,
          d.fb && d.fb.restaurants && d.fb.restaurants.length > 0 ? "food" : null,
          d.destination && d.destination.length > 0 ? "destination" : null,
        ].filter(Boolean)}
        isBivouac={bivouac}
      />

      {/* QUICK BOOK PILL — zero-scroll shortcut. Prefills the shared
          enquiry with this hotel + chosen dates/adults, then scrolls
          the visitor to the full Plan-Your-Stay widget. */}
      <QuickBookingPill hotelSlug={hotel.slug} />

      {/* ABOUT HERO — independent admin-managed banner above the About
          section. Only renders when the admin has uploaded a hero image. */}
      {hasAnyHero(propertyGalleries, "about") && (
        <section
          className="relative w-full overflow-hidden"
          data-testid="about-hero-banner"
          data-cms-scope={`hotel.${hotel.slug}.about`}
        >
          <div className="relative aspect-[21/9] md:aspect-[24/8] w-full bg-[#1A1513]/10">
            <img
              src={getHeroUrl(propertyGalleries, "about")}
              alt={`${hotel.name} — About`}
              data-cms-role="about-banner"
              className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/45 via-transparent to-transparent" />
          </div>
        </section>
      )}

      {/* ABOUT */}
      <Section
        id="about"
        overline={t("sections.hotels_overline")}
        title={t("hotel.about")}
        intro={pick(hotel.intro, lang)}
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="font-serif-x text-2xl md:text-3xl leading-[1.35] text-[#2C2621]">
              {pick(d.story, lang)}
            </p>
            <p className="mt-8 text-lg text-[#5C5248] leading-relaxed">
              {pick(d.architecture, lang)}
            </p>
            <figure className="mt-12 border-l-2 border-[#C16542] pl-6" data-testid="founder-quote">
              <p className="overline">{t("hotel.founder")}</p>
              <blockquote className="mt-3 font-serif-x text-xl md:text-2xl italic text-[#2C2621] leading-snug">
                {pick(d.founder_quote, lang)}
              </blockquote>
            </figure>
            <HotelReviews hotelSlug={hotel.slug} />
          </div>
          <div className="md:col-span-5">
            {/* About-the-Hotel in-content gallery — sources tiles from the
                admin-managed `about::gallery` collection (independent of the
                hero banner above). Falls back to hard-coded hotel imagery
                when the gallery is empty. */}
            <div className="grid grid-cols-2 gap-3" data-testid="about-hotel-gallery" data-cms-scope={`hotel.${hotel.slug}.about-gallery`}>
              {(() => {
                const fallback = [hotel.image, ...(hotel.gallery || [])].filter(Boolean);
                const tiles = buildAboutTiles(propertyGalleries, "about", fallback);
                if (!tiles) return null;
                return tiles.map((g, i) => (
                  <div
                    key={`founder-gallery-${i}`}
                    className={`relative overflow-hidden ${i === 0 ? "col-span-2 aspect-[16/10]" : "aspect-square"}`}
                    data-testid={`about-hotel-gallery-${i}`}
                  >
                    <img
                      src={g}
                      alt={`${hotel.name} ${i + 1}`}
                      data-cms-role={`tile.${i}`}
                      className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"/>
                  </div>
                ));
              })()}
            </div>

            {/* Property location map — pinned to this specific slug,
                sits under the image grid inside the same visual column. */}
            <HotelAboutMap
              hotelSlug={hotel.slug}
              hotelName={hotel.name}
              hotelLocation={hotel.location}
            />

            {/* AI Concierge invitation — opens the on-page chat widget
                (same global `xaluca:open-chat` event used elsewhere). */}
            <HotelConciergeCTA hotelSlug={hotel.slug} hotelName={hotel.name} />
          </div>
        </div>
      </Section>

      {/* DESERT CAMPS — three distinct camp experiences for la-belle-etoile-bivouacs */}
      {d.camps && d.camps.length > 0 && (
        <section className="py-20 md:py-28" data-testid="section-desert-camps" id="camps">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
              <div className="md:col-span-7">
                <p className="overline">
                  {pick({ en: "Three desert camps", fr: "Trois camps du désert", es: "Tres campamentos del desierto" }, lang)}
                </p>
                <h2 className="font-serif-x mt-3 text-4xl md:text-6xl tracking-tight leading-[1.05]">
                  {pick({
                    en: "Choose the night you want to live.",
                    fr: "Choisissez la nuit que vous voulez vivre.",
                    es: "Elija la noche que quiere vivir.",
                  }, lang)}
                </h2>
              </div>
              <div className="md:col-span-5 md:flex md:items-end">
                <p className="text-base md:text-lg text-[#5C5248] leading-relaxed">
                  {pick({
                    en: "From the candle-lit authenticity of our oldest camp to the cocktail-table sophistication of our flagship, to the perfectly private hush of our mini-camp for celebrations — three Sahrawi camps, one family, one star horizon.",
                    fr: "De l'authenticité aux chandelles de notre camp historique au raffinement-cocktail de notre vaisseau amiral, en passant par le silence parfaitement privé de notre mini-camp pour célébrations — trois camps sahraouis, une famille, un même horizon d'étoiles.",
                    es: "De la autenticidad a la luz de las velas de nuestro campamento histórico al refinamiento de cóctel de nuestro buque insignia, pasando por el silencio perfectamente privado de nuestro mini-campamento para celebraciones — tres campamentos saharauis, una familia, un mismo horizonte estelar.",
                  }, lang)}
                </p>
              </div>
            </div>

            <div className="space-y-8 md:space-y-12">
              {d.camps.map((camp, idx) => {
                const tone = camp.tone || "earth";
                // Per-camp tone palette — earth / luxury / intimate
                const palette = {
                  earth: { bg: "bg-[#F2EBE1]/60", text: "text-[#2C2621]", muted: "text-[#5C5248]", accent: "text-[#C16542]", border: "border-[#C16542]/30", btn: "bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7]" },
                  luxury: { bg: "bg-[#1A1513]", text: "text-[#FDFBF7]", muted: "text-[#FDFBF7]/75", accent: "text-[#D4A373]", border: "border-[#D4A373]/40", btn: "bg-[#D4A373] hover:bg-[#C2925E] text-[#1A1513]" },
                  intimate: { bg: "bg-[#2C2621]", text: "text-[#FDFBF7]", muted: "text-[#FDFBF7]/70", accent: "text-[#E8C9A0]", border: "border-[#E8C9A0]/40", btn: "bg-[#E8C9A0] hover:bg-[#D4A373] text-[#1A1513]" },
                }[tone];
                const reverse = idx % 2 === 1;
                const campName = pick(camp.name, lang);
                const highlights = (camp.highlights && (camp.highlights[lang] || camp.highlights.en)) || [];
                return (
                  <article
                    key={`camp-${camp.slug}`}
                    className={`${palette.bg} ${palette.text} overflow-hidden`}
                    data-testid={`camp-${camp.slug}`}
                    data-cms-scope={`hotel.${hotel.slug}.camp.${camp.slug}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 items-stretch">
                      <div className={`md:col-span-6 ${reverse ? "md:order-2" : ""}`}>
                        <CampImageGallery
                          images={camp.gallery && camp.gallery.length > 0 ? camp.gallery : [camp.image]}
                          alt={campName}
                          cmsScope={`hotel.${hotel.slug}.camp.${camp.slug}.gallery`}
                          testIdBase={`camp-${camp.slug}`}
                          accentClass={tone === "earth" ? "border-[#C16542]" : tone === "luxury" ? "border-[#D4A373]" : "border-[#E8C9A0]"}
                          overlay={
                            <div className="absolute top-5 left-5 z-10 pointer-events-none">
                              <span className="inline-block bg-[#1A1513]/85 text-[#FDFBF7] text-[10px] tracking-[0.25em] uppercase px-3 py-1.5">
                                {String(idx + 1).padStart(2, "0")} · {pick(camp.kind, lang)}
                              </span>
                            </div>
                          }
                        />
                      </div>
                      <div className={`md:col-span-6 p-8 md:p-12 lg:p-16 flex flex-col justify-center ${reverse ? "md:order-1" : ""}`}>
                        <p className={`overline ${palette.accent === "text-[#C16542]" ? "" : "!" + palette.accent}`}>
                          {pick(camp.eyebrow, lang)}
                        </p>
                        <h3 className="font-serif-x mt-3 text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                          {campName}
                        </h3>
                        <p className={`mt-5 text-base md:text-lg leading-relaxed ${palette.muted}`}>
                          {pick(camp.intro, lang)}
                        </p>
                        <p className={`mt-3 text-[15px] leading-relaxed ${palette.muted}`}>
                          {pick(camp.body, lang)}
                        </p>

                        {/* Practical strip */}
                        <div className={`mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-5 border-t ${palette.border}`}>
                          <div>
                            <p className={`overline ${palette.accent === "text-[#C16542]" ? "" : "!" + palette.accent}`}>
                              {pick({ en: "Accommodation", fr: "Hébergement", es: "Alojamiento" }, lang)}
                            </p>
                            <p className={`mt-2 text-sm ${palette.muted}`}>{pick(camp.accommodation, lang)}</p>
                          </div>
                          <div>
                            <p className={`overline ${palette.accent === "text-[#C16542]" ? "" : "!" + palette.accent}`}>
                              {pick({ en: "Capacity", fr: "Capacité", es: "Capacidad" }, lang)}
                            </p>
                            <p className={`mt-2 text-sm ${palette.muted}`}>{pick(camp.capacity, lang)}</p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className={`overline ${palette.accent === "text-[#C16542]" ? "" : "!" + palette.accent}`}>
                              {pick({ en: "Ideal for", fr: "Idéal pour", es: "Ideal para" }, lang)}
                            </p>
                            <p className={`mt-2 text-sm ${palette.muted}`}>{pick(camp.ideal, lang)}</p>
                          </div>
                        </div>

                        {/* Highlights bullets */}
                        {highlights.length > 0 && (
                          <ul className="mt-6 space-y-2">
                            {highlights.map((h, hi) => (
                              <li key={`${camp.slug}-h-${hi}`} className={`flex items-start gap-3 text-[14px] ${palette.muted}`}>
                                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${palette.accent}`} strokeWidth={2} />
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <div className="mt-8 flex flex-wrap items-center gap-4">
                          <Link
                            to={`/booking?hotel=${encodeURIComponent(hotel.slug)}&camp=${encodeURIComponent(camp.slug)}`}
                            className={`${palette.btn} px-7 py-3.5 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2`}
                            data-testid={`camp-${camp.slug}-cta`}
                          >
                            {pick({ en: "Request this camp", fr: "Demander ce camp", es: "Solicitar este campamento" }, lang)}
                            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Link>
                          <span className={`text-sm ${palette.accent}`}>{parsePrice(pick(camp.price, lang)) || pick(camp.price, lang)}</span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* DIRECTOR'S MESSAGE — personal letter from your host */}
      {d.director && (
        <section className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7]" data-testid="section-director-message" data-cms-scope={`hotel.${hotel.slug}.director`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-4 order-2 lg:order-1">
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden rounded-sm">
                  <img
                    src={d.director.photo}
                    alt={d.director.name}
                    className="w-full h-full object-cover"
                    data-testid="director-photo" loading="lazy" decoding="async"
                    data-cms-role="portrait"/>
                </div>
                <div className="absolute -bottom-3 -right-3 bg-[#C16542] text-[#FDFBF7] px-5 py-3 text-[10px] tracking-[0.3em] uppercase">
                  {pick(d.director.greeting, lang)}
                </div>
              </div>
            </div>
            <div className="lg:col-span-8 order-1 lg:order-2">
              <p className="overline !text-[#D4A373]">
                {pick({ en: "A word from your host", fr: "Le mot de votre hôte", es: "Unas palabras de su anfitrión" }, lang)}
              </p>
              <Quote className="w-7 h-7 mt-5 text-[#D4A373]" strokeWidth={1.5} />
              <p
                className="mt-5 font-serif-x text-2xl md:text-3xl lg:text-4xl leading-[1.3] tracking-tight text-[#FDFBF7] whitespace-pre-line"
                data-testid="director-message"
              >
                {pick(d.director.message, lang)}
              </p>
              <div className="mt-8 pt-6 border-t border-[#FDFBF7]/15 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4A373]">
                    {pick(d.director.signature, lang)}
                  </p>
                  <p className="font-serif-x text-2xl md:text-3xl mt-2 text-[#FDFBF7]" data-testid="director-name">
                    {d.director.name}
                  </p>
                  <p className="mt-1 text-[12px] tracking-[0.2em] uppercase text-[#FDFBF7]/65">
                    {pick(d.director.role, lang)}
                  </p>
                </div>
                <Link
                  to={`/booking?mode=single&hotel=${hotel.slug}`}
                  className="inline-flex items-center gap-2 self-start sm:self-auto border border-[#D4A373]/50 text-[#FDFBF7] px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C16542] hover:border-[#C16542] transition-colors"
                  data-testid="director-cta"
                >
                  {pick({ en: "Write to my team", fr: "Écrire à mon équipe", es: "Escribir a mi equipo" }, lang)}
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HOTEL VIDEOS — moved here so the cinematic block appears in the first main content area, right after the host's letter */}
      {(() => {
        const CATEGORY_KEY = { bivouac: "bivouac", refuge: "refuge" };
        const cat = CATEGORY_KEY[hotel.category] || "kasbah";
        const pickByCat = (m) => m[cat] ?? m.kasbah;
        const g = hotel.gallery || [];
        const posters = {
          presentation: hotel.image,
          gastronomy: "https://images.unsplash.com/photo-1541518763669-27fef04b14ea?auto=format&fit=crop&w=1600&q=80",
          wellness: "https://images.unsplash.com/photo-1591343395082-e120087004b4?auto=format&fit=crop&w=1600&q=80",
          drone: g[1] || g[0] || hotel.image,
          lifestyle: g[2] || g[0] || hotel.image,
          heritage: g[3] || g[2] || g[0] || hotel.image,
          excursions: pickByCat({
            bivouac: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80",
            refuge: "https://images.unsplash.com/photo-1551632811-561732d1e306?auto=format&fit=crop&w=1600&q=80",
            kasbah: "https://images.unsplash.com/photo-1517144990192-c1f190534ac6?auto=format&fit=crop&w=1600&q=80",
          }),
        };

        const featuredCopy = pickByCat({
          bivouac: {
            title: {
              en: `${hotel.name} — a night on the dunes`,
              fr: `${hotel.name} — une nuit sur les dunes`,
              es: `${hotel.name} — una noche en las dunas`,
            },
            tag: { en: "Bivouac film", fr: "Film du bivouac", es: "Film del vivac" },
          },
          refuge: {
            title: {
              en: `${hotel.name} — into the M'Goun`,
              fr: `${hotel.name} — au cœur du M'Goun`,
              es: `${hotel.name} — en el corazón del M'Goun`,
            },
            tag: { en: "Refuge film", fr: "Film du refuge", es: "Film del refugio" },
          },
          kasbah: {
            title: {
              en: `${hotel.name} — a property in motion`,
              fr: `${hotel.name} — l'adresse en mouvement`,
              es: `${hotel.name} — la propiedad en movimiento`,
            },
            tag: { en: "Hotel presentation", fr: "Présentation de l'hôtel", es: "Presentación del hotel" },
          },
        });

        const wellnessTitle = pickByCat({
          bivouac: { en: "Desert rituals", fr: "Rituels du désert", es: "Rituales del desierto" },
          refuge: { en: "Hammam & wellness", fr: "Hammam & bien-être", es: "Hammam y bienestar" },
          kasbah: { en: "Hammam & wellness", fr: "Hammam & bien-être", es: "Hammam y bienestar" },
        });
        const wellnessDesc = pickByCat({
          bivouac: {
            en: "Foot-baths of warm water and rose petals, oil-of-argan massages under the open sky.",
            fr: "Bains de pieds à l'eau chaude et pétales de rose, massages à l'huile d'argan à ciel ouvert.",
            es: "Baños de pies con agua caliente y pétalos de rosa, masajes con aceite de argán al aire libre.",
          },
          refuge: {
            en: "Steam, Beldi black-soap scrubs and argan rituals in our hammam cabins.",
            fr: "Vapeur, gommages au savon noir Beldi et rituels à l'argan dans nos cabines de hammam.",
            es: "Vapor, exfoliaciones con jabón negro Beldi y rituales de argán en nuestras cabinas.",
          },
          kasbah: {
            en: "Steam, Beldi black-soap scrubs and argan rituals in our hammam cabins.",
            fr: "Vapeur, gommages au savon noir Beldi et rituels à l'argan dans nos cabines de hammam.",
            es: "Vapor, exfoliaciones con jabón negro Beldi y rituales de argán en nuestras cabinas.",
          },
        });
        const droneTitle = pickByCat({
          bivouac: { en: "Aerial over Erg Chebbi", fr: "Vue aérienne sur l'Erg Chebbi", es: "Vista aérea sobre el Erg Chebbi" },
          refuge: { en: "Aerial over the M'Goun", fr: "Vue aérienne sur le M'Goun", es: "Vista aérea sobre el M'Goun" },
          kasbah: { en: "Aerial of the property", fr: "Vue aérienne de l'adresse", es: "Vista aérea de la propiedad" },
        });
        const excursionsTitle = pickByCat({
          bivouac: { en: "Camel caravans & sandboarding", fr: "Caravanes & sandboard", es: "Caravanas y sandboard" },
          refuge: { en: "Treks in the M'Goun canyons", fr: "Treks dans les canyons du M'Goun", es: "Treks en los cañones del M'Goun" },
          kasbah: { en: "4×4, treks and valley drives", fr: "4×4, treks et vallées", es: "4×4, treks y valles" },
        });
        const heritageTitle = pickByCat({
          bivouac: { en: "Music under the stars", fr: "La musique sous les étoiles", es: "Música bajo las estrellas" },
          refuge: { en: "Mountain artisans", fr: "Artisans de la montagne", es: "Artesanos de la montaña" },
          kasbah: { en: "The craft of the house", fr: "Les artisans de la maison", es: "Los artesanos de la casa" },
        });
        const heritageDesc = pickByCat({
          bivouac: {
            en: "Sahrawi musicians around the fire — lutes, hand-clapped songs, the sound of the desert at night.",
            fr: "Musiciens sahraouis autour du feu — luths, chants frappés, le son du désert à la nuit tombée.",
            es: "Músicos saharauis al fuego — laúdes, cantos a palmas, el sonido del desierto al caer la noche.",
          },
          refuge: {
            en: "Berber weavers, shepherds and bread-makers — the everyday hands that keep the valleys alive.",
            fr: "Tisserands berbères, bergers et boulangères — les mains du quotidien qui font vivre les vallées.",
            es: "Tejedoras bereberes, pastores y panaderas — las manos cotidianas que mantienen vivos los valles.",
          },
          kasbah: {
            en: "Zellige masters, cedar carvers and tadelakt artisans at work in the kasbah's living workshops.",
            fr: "Maîtres zelligers, sculpteurs de cèdre et artisans du tadelakt dans les ateliers vivants de la kasbah.",
            es: "Maestros del zellige, talladores de cedro y artesanos del tadelakt en los talleres vivos de la kasbah.",
          },
        });
        const heritageTag = pickByCat({
          bivouac: { en: "Music & rituals", fr: "Musique & rituels", es: "Música y rituales" },
          refuge: { en: "Artisans", fr: "Artisans", es: "Artesanos" },
          kasbah: { en: "Heritage crafts", fr: "Artisanat", es: "Artesanía" },
        });

        const secondary = [
          {
            id: `hotel-${hotel.slug}-gastronomy`,
            title: { en: "At the table", fr: "À table", es: "En la mesa" },
            desc: {
              en: "Tagines slow-cooked over embers, the mint-tea ritual, breads still warm from the clay oven.",
              fr: "Tajines mijotés à la braise, rituel du thé à la menthe, pains tièdes sortis du four en argile.",
              es: "Tajines a la brasa, ritual del té con menta, panes tibios del horno de barro.",
            },
            duration: "1:48",
            tag: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" },
            poster: posters.gastronomy,
          },
          {
            id: `hotel-${hotel.slug}-wellness`,
            title: wellnessTitle,
            desc: wellnessDesc,
            duration: "1:32",
            tag: { en: "Wellness", fr: "Bien-être", es: "Bienestar" },
            poster: posters.wellness,
          },
          {
            id: `hotel-${hotel.slug}-drone`,
            title: droneTitle,
            desc: {
              en: "Drone footage tracing the architecture, the palm groves and the wider landscape from above.",
              fr: "Images drone qui suivent l'architecture, les palmeraies et le paysage alentour, vus d'en haut.",
              es: "Imágenes con dron de la arquitectura, los palmerales y el paisaje circundante, vistos desde arriba.",
            },
            duration: "1:15",
            tag: { en: "Drone footage", fr: "Images drone", es: "Imágenes con dron" },
            poster: posters.drone,
          },
          {
            id: `hotel-${hotel.slug}-storytelling`,
            title: { en: "A day in the life", fr: "Une journée dans nos murs", es: "Un día en nuestras casas" },
            desc: {
              en: "First light over the kasbah, the slow walk to breakfast, the sound of the muezzin at dusk.",
              fr: "Premières lueurs sur la kasbah, la marche lente vers le petit-déjeuner, le muezzin au crépuscule.",
              es: "Primera luz sobre la kasbah, el lento paseo al desayuno, el muecín al crepúsculo.",
            },
            duration: "2:42",
            tag: { en: "Storytelling", fr: "Récit", es: "Relato" },
            poster: posters.lifestyle,
          },
          {
            id: `hotel-${hotel.slug}-excursions`,
            title: excursionsTitle,
            desc: {
              en: "Short films from the excursions and the wider region — what guests do when they leave the property.",
              fr: "Courts films des excursions et de la région — ce que font nos hôtes lorsqu'ils sortent de l'adresse.",
              es: "Cortos de las excursiones y la región — lo que hacen nuestros huéspedes al salir de la propiedad.",
            },
            duration: "2:12",
            tag: { en: "Excursions", fr: "Excursions", es: "Excursiones" },
            poster: posters.excursions,
          },
          {
            id: `hotel-${hotel.slug}-heritage`,
            title: heritageTitle,
            desc: heritageDesc,
            duration: "1:58",
            tag: heritageTag,
            poster: posters.heritage,
          },
        ];

        return (
          <VideoSection
            overline={{ en: "Films from the property", fr: "Films de l'adresse", es: "Films de la propiedad" }}
            title={{
              en: `Inside ${hotel.name}.`,
              fr: `Au cœur de ${hotel.name}.`,
              es: `Dentro de ${hotel.name}.`,
            }}
            intro={{
              en: "Seven short films covering the property — its presentation, the table, wellness rituals, aerial footage, lifestyle, the excursions around it, and the artisans and music behind the place.",
              fr: "Sept courts films autour de l'adresse — sa présentation, la table, les rituels bien-être, les images drone, l'art de vivre, les excursions alentour et les artisans et la musique qui font ce lieu.",
              es: "Siete cortos sobre la propiedad — su presentación, la mesa, los rituales de bienestar, las imágenes aéreas, el arte de vivir, las excursiones cercanas y los artesanos y la música que dan vida al lugar.",
            }}
            featured={{
              id: `hotel-${hotel.slug}-feat`,
              title: featuredCopy.title,
              desc: pick(hotel.intro, lang),
              duration: "2:34",
              tag: featuredCopy.tag,
              poster: posters.presentation,
            }}
            secondary={secondary}
            testid={`hotel-${hotel.slug}-videos`}
          />
        );
      })()}

      {/* HIGHLIGHTS / QUICK FACTS */}
      <section className="bg-[#F2EBE1]/60 py-16 md:py-20 border-y border-[#2C2621]/10" data-testid="section-highlights">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline">{t("hotel.highlights")}</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: BedDouble, label: accomTerms.stat_label, value: d.stats.rooms },
              { icon: Maximize, label: t("hotel.stat_surface"), value: d.stats.surface },
              { icon: Plane, label: t("hotel.stat_airport"), value: d.stats.airport },
              { icon: Compass, label: t("hotel.stat_location"), value: d.stats.coords },
            ].map((s, i) => (
              <div key={`stat-${s.label}`} className="flex flex-col gap-2" data-testid={`stat-${i}`}>
                <s.icon className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
                <div className="font-serif-x text-2xl md:text-3xl text-[#2C2621] leading-tight">
                  {s.value}
                </div>
                <div className="overline !text-[#5C5248]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL TIME & WEATHER — live Morocco clock + Open-Meteo for the property's
          exact coordinates. Includes a desert-context tip for bivouacs. */}
      <LocalTimeWeatherWidget
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
        isBivouac={hotel.category === "bivouac"}
        testIdPrefix={`hotel-${hotel.slug}`}
      />

      {/* ROOMS */}
      <Section
        id="rooms"
        overline={t("sections.hotels_overline")}
        title={accomTerms.section_title}
        intro={accomTerms.section_intro}
      >
        {(() => {
          // Multi-camp properties (currently: La Belle Étoile Bivouacs)
          // expose a `camp` slug on each room; render a tab bar so guests
          // can switch between the sets. Falls back gracefully to the
          // single-list rendering for every other hotel.
          const roomsHaveCamp = d.rooms.some((r) => !!r.camp);
          const campTabs = roomsHaveCamp && Array.isArray(d.camps)
            ? d.camps.filter((c) => d.rooms.some((r) => r.camp === c.slug))
            : [];
          const currentCampSlug =
            campTabs.length > 0
              ? (campTabs.some((c) => c.slug === activeCampSlug) ? activeCampSlug : campTabs[0].slug)
              : null;
          const visibleRooms = currentCampSlug
            ? d.rooms.filter((r) => !r.camp || r.camp === currentCampSlug)
            : d.rooms;

          return (
            <>
              {campTabs.length > 1 && (
                <div
                  role="tablist"
                  aria-label={pick({ en: "Choose a camp", fr: "Choisir un camp", es: "Elegir un campamento" }, lang)}
                  className="mb-10 flex flex-wrap gap-2 border-b border-[#2C2621]/15"
                  data-testid="rooms-camp-tabs"
                >
                  {campTabs.map((c) => {
                    const isActive = c.slug === currentCampSlug;
                    return (
                      <button
                        key={`camp-tab-${c.slug}`}
                        role="tab"
                        type="button"
                        aria-selected={isActive}
                        aria-controls={`rooms-panel-${c.slug}`}
                        onClick={() => setActiveCampSlug(c.slug)}
                        data-testid={`rooms-camp-tab-${c.slug}`}
                        className={`relative -mb-px px-4 md:px-5 py-3.5 text-[10px] md:text-xs tracking-[0.25em] uppercase transition-colors border-b-2 ${
                          isActive
                            ? "border-[#C16542] text-[#2C2621]"
                            : "border-transparent text-[#5C5248] hover:text-[#2C2621] hover:border-[#2C2621]/20"
                        }`}
                      >
                        {pick(c.name, lang)}
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                id={currentCampSlug ? `rooms-panel-${currentCampSlug}` : undefined}
                role={currentCampSlug ? "tabpanel" : undefined}
                className="space-y-12"
                data-testid="rooms-list"
              >
                {(() => {
                  // Group visibleRooms by variantGroup preserving order.
                  // Rooms without variantGroup form their own single-item
                  // group so the render loop stays uniform.
                  const roomGroups = [];
                  const groupIndex = new Map();
                  for (const r of visibleRooms) {
                    const key = r.variantGroup || `__solo-${slugifyRoom(r)}`;
                    if (groupIndex.has(key)) {
                      roomGroups[groupIndex.get(key)].variants.push(r);
                    } else {
                      groupIndex.set(key, roomGroups.length);
                      roomGroups.push({ key, variants: [r] });
                    }
                  }
                  return roomGroups.map((group, i) => {
                    const hasVariants = group.variants.length > 1;
                    const rawActive = activeVariants[group.key];
                    const activeIdx = hasVariants && Number.isInteger(rawActive) && rawActive >= 0 && rawActive < group.variants.length
                      ? rawActive
                      : 0;
                    const activeRoom = group.variants[activeIdx];
                    const roomImages = getRoomImagesFromAdmin(activeRoom, hotel, adminGalleries);
                    const setVariant = (idx) => setActiveVariants((prev) => ({ ...prev, [group.key]: idx }));
                    return (
                      <article
                        key={`room-group-${group.key}`}
                        className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-center ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
                        data-testid={`room-${i}`}
                      >
                        <div className="md:col-span-7">
                          <RoomImageGallery
                            key={`gallery-${group.key}-${activeIdx}`}
                            images={roomImages.length ? roomImages : [activeRoom.image].filter(Boolean)}
                            alt={pick(activeRoom.name, lang)}
                            aspect="aspect-[16/10]"
                            testIdPrefix={`room-${i}-gallery`}
                            managedGallery={{ hotelSlug: hotel.slug, roomSlug: slugifyRoom(activeRoom) }}
                          />
                        </div>
                        <div className="md:col-span-5">
                          <p className="overline">{String(i + 1).padStart(2, "0")} · {accomTerms.section_title}</p>
                          <h3 className="font-serif-x text-3xl md:text-4xl mt-2 leading-tight">
                            {pick(activeRoom.name, lang)}
                          </h3>
                          {hasVariants && (
                            <div
                              role="tablist"
                              aria-label={pick(
                                { en: "Choose a variant", fr: "Choisir une variante", es: "Elegir una variante" },
                                lang,
                              )}
                              className="mt-4 flex flex-wrap gap-1 border-b border-[#2C2621]/10"
                              data-testid={`room-${i}-variant-tabs`}
                            >
                              {group.variants.map((v, vIdx) => {
                                const isActive = vIdx === activeIdx;
                                return (
                                  <button
                                    key={`variant-${v.slug || vIdx}`}
                                    role="tab"
                                    type="button"
                                    aria-selected={isActive}
                                    onClick={() => setVariant(vIdx)}
                                    data-testid={`room-${i}-variant-tab-${vIdx}`}
                                    className={`-mb-px px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase transition-colors border-b-2 ${
                                      isActive
                                        ? "border-[#C16542] text-[#2C2621]"
                                        : "border-transparent text-[#5C5248] hover:text-[#2C2621] hover:border-[#2C2621]/20"
                                    }`}
                                  >
                                    {pick(v.variantLabel, lang) || `Option ${vIdx + 1}`}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <div className={`${hasVariants ? "mt-4" : "mt-4"} flex items-center gap-5 text-sm text-[#5C5248]`}>
                            <span className="flex items-center gap-1.5">
                              <Maximize className="w-4 h-4 text-[#C16542]" strokeWidth={1.5} /> {pick(activeRoom.surface, lang)}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <Users className="w-4 h-4 text-[#C16542]" strokeWidth={1.5} /> {pick(activeRoom.capacity, lang)}
                            </span>
                          </div>
                          <p className="mt-5 text-base text-[#5C5248] leading-relaxed">
                            {pick(activeRoom.desc, lang)}
                          </p>
                          <RoomAmenitiesStrip
                            room={activeRoom}
                            lang={lang}
                            testIdPrefix={`room-${i}-amenities`}
                          />
                          <div className="mt-6">
                            <RoomPriceTag
                              room={activeRoom}
                              hotelSlug={hotel.slug}
                              testid={`room-price-tag-${i}`}
                            />
                          </div>
                          <div className="mt-7 flex flex-wrap items-center gap-5">
                            <button
                              type="button"
                              onClick={() => setGalleryRoom(activeRoom)}
                              className="inline-flex items-center gap-2 border border-[#2C2621]/30 px-4 py-2.5 text-xs tracking-[0.25em] uppercase hover:border-[#C16542] hover:text-[#C16542] transition-colors"
                              data-testid={`room-gallery-${i}`}
                            >
                              <Images className="w-3.5 h-3.5" strokeWidth={1.5} />
                              {pick(
                                { en: "View gallery", fr: "Voir la galerie", es: "Ver galería" },
                                lang
                              )}
                            </button>
                            <Link
                              to={`/booking?hotel=${encodeURIComponent(hotel.slug)}`}
                              className="inline-block border-b border-[#2C2621]/30 pb-1 text-xs tracking-[0.25em] uppercase hover:border-[#C16542] hover:text-[#C16542] transition-colors"
                              data-testid={`room-book-${i}`}
                            >
                              {accomTerms.book_cta} →
                            </Link>
                          </div>
                          <ExperienceTestimonial
                            testimonial={pickRoomTestimonial(
                              pick(activeRoom.name, lang) || activeRoom.name,
                              i,
                              adminRoomTestimonials,
                              testimonialSeed + i
                            )}
                            lang={lang}
                            variant="light"
                            testId={`room-${i}-testimonial`}
                            cmsScope={`hotel.${hotel.slug}.room.${i}.testimonial`}
                          />
                        </div>
                      </article>
                    );
                  });
                })()}
              </div>
            </>
          );
        })()}
      </Section>

      {/* ROOM RATES — extracted editorial strip with per-row board dropdowns.
          Placed right after the Rooms section so guests see pricing in the
          same visual flow where they discover the accommodations. */}
      <RoomRatesStrip rooms={d.rooms} hotelSlug={hotel.slug} />

      {/* CURATED GALLERY — fed by /admin/curated-galleries; the section
          silently no-ops for hotels that haven't been curated yet. The
          data is loaded once by the parent `useCuratedGallery` hook and
          shared with the sticky-nav availability check. */}
      <CuratedGallerySection
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
        lang={lang}
        images={curated.images}
        loading={curated.loading}
      />

      {/* BROCHURE — per-property Publuu flip-book. Silently no-ops when
          the hotel doesn't have a brochure yet. */}
      <HotelBrochureSection
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
        lang={lang}
      />

      {/* SERVICES — hidden on bivouacs/desert camps (hotel-only content) */}
      {!bivouac && (
      <section id="services" className="py-20 md:py-28 bg-[#F2EBE1]/40" data-testid="section-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <p className="overline">{t("hotel.services")}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
                {t("hotel.services")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {d.services.map((s, i) => {
              const Icon = ICONS[s.icon] || Sparkles;
              const thumbFallback = pickThumb("services", i, s.image);
              const thumbSlot = `hotel.${hotel.slug}.services.${s.slug || i}.thumb`;
              const inner = (
                <>
                  <EditableImage
                    slot={thumbSlot}
                    fallback={thumbFallback}
                    alt={pick(s.title, lang)}
                    aspectRatio="16/9"
                    wrapperClassName="block -mx-8 -mt-8 mb-6"
                    className="block w-full"
                    imgClassName="w-full h-full object-cover"
                  />
                  <Icon className="w-7 h-7 text-[#C16542]" strokeWidth={1.5} />
                  <h3 className="font-serif-x text-2xl mt-5 leading-tight">
                    {pick(s.title, lang)}
                  </h3>
                  <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">
                    {pick(s.desc, lang)}
                  </p>
                  {s.slug && (
                    <div className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/30 pb-1 w-fit group-hover:border-[#C16542] group-hover:text-[#C16542] transition-colors">
                      {pick({ en: "View details", fr: "Voir les détails", es: "Ver detalles" }, lang)}
                      <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </div>
                  )}
                </>
              );
              return s.slug ? (
                <Link
                  key={`service-${s.slug}`}
                  to={`/hotels/${hotel.slug}/services/${s.slug}`}
                  className="group bg-[#FDFBF7] border border-[#2C2621]/10 p-8 hover:border-[#C16542]/40 transition-colors block overflow-hidden"
                  data-testid={`service-${i}`}
                >
                  {inner}
                </Link>
              ) : (
                <div
                  key={`service-${s.title?.en || i}`}
                  className="bg-[#FDFBF7] border border-[#2C2621]/10 p-8 overflow-hidden"
                  data-testid={`service-${i}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* SPA & WELLNESS — featured editorial section for hotels that operate a full spa · hidden on bivouacs */}
      {!bivouac && (
      <SpaWellnessFeature
        feature={getSpaFeature(hotel.slug)}
        spaMenu={d.spa_menu}
        hotelSlug={hotel.slug}
        hotelName={hotel.name}
      />
      )}

      {/* SPA PRICE STRIP — relocated here so it appears inside the Spa & Wellness section
          (previously rendered directly below the hotel hero). Same visual structure as the
          Room Rates strip up top. Auto-renders for hotels with MAD-priced spa menu.
          Hidden on bivouacs. */}
      {!bivouac && d.spa_menu && d.spa_menu.some((s) => typeof s.price === "string" && s.price.includes("MAD")) && (
        <section
          className="py-16 md:py-20 bg-[#F2EBE1]/40 border-b border-[#2C2621]/10"
          data-testid="spa-price-strip"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="bg-[#FDFBF7] border border-[#E8DFCF]">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
                <div className="md:col-span-4 bg-[#1A1513] text-[#FDFBF7] p-8 md:p-10 flex flex-col justify-between">
                  <div>
                    <p className="overline !text-[#D4A373] flex items-center gap-2">
                      <Flame className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {pick({ en: "Spa & Hammam", fr: "Spa & Hammam", es: "Spa y Hammam" }, lang)}
                    </p>
                    <h3 className="font-serif-x mt-3 text-3xl md:text-4xl leading-[1.05]">
                      {pick(
                        { en: "Rituals at the spa", fr: "Rituels au spa", es: "Rituales en el spa" },
                        lang
                      )}
                    </h3>
                    <p className="mt-4 text-sm text-[#FDFBF7]/70 leading-relaxed">
                      {pick(
                        {
                          en: "Heated waters, Berber hammam, signature massages — each treatment bookable on arrival or via the concierge.",
                          fr: "Eaux chauffées, hammam berbère, massages signature — chaque soin réservable à l'arrivée ou via la conciergerie.",
                          es: "Aguas climatizadas, hammam bereber, masajes signature — cada tratamiento reservable a la llegada o vía conserjería.",
                        },
                        lang
                      )}
                    </p>
                  </div>
                  <Link
                    to={`/booking?hotel=${encodeURIComponent(hotel.slug)}`}
                    className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4A373] hover:text-[#FDFBF7] w-fit"
                    data-testid="spa-price-book"
                  >
                    {pick(
                      { en: "Reserve your ritual", fr: "Réserver votre rituel", es: "Reservar su ritual" },
                      lang
                    )}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                </div>
                <div className="md:col-span-8 p-6 md:p-8">
                  <ul className="divide-y divide-[#E8DFCF]" data-testid="spa-price-list">
                    {d.spa_menu.slice(0, 7).map((s) => (
                      <li
                        key={`spa-${(s.title?.en || s.title || "ritual")}-${s.duration || ""}`}
                        className="flex items-baseline gap-4 py-3 md:py-3.5"
                      >
                        <span className="font-serif-x text-base md:text-lg text-[#2C2621] flex-1 leading-snug">
                          {pick(s.title, lang)}
                        </span>
                        <span className="text-xs tracking-[0.2em] uppercase text-[#5C5248]/70 hidden sm:inline">
                          {s.duration}
                        </span>
                        <span className="text-sm md:text-base font-medium text-[#C16542] tabular-nums whitespace-nowrap min-w-[5rem] text-right">
                          {parsePrice(s.price) || s.price}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-xs text-[#5C5248]/70 italic">
                    {pick(
                      {
                        en: `Prices in ${currency} · all treatments bookable on arrival or via the concierge.`,
                        fr: `Tarifs en ${currency} · tous les soins réservables à l'arrivée ou via la conciergerie.`,
                        es: `Tarifas en ${currency} · todos los tratamientos reservables a la llegada o vía conserjería.`,
                      },
                      lang
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ROOM & SERVICE DETAILS — public starting rates in MAD */}
      <RoomServicePricing rooms={d.rooms} services={d.services} hotelSlug={hotel.slug} adminGalleries={adminGalleries} />

      {/* EXPERIENCES */}
      <Section
        id="experiences"
        overline={t("sections.experiences_overline")}
        title={t("hotel.experiences")}
        intro={pick({
          en: "Curated excursions designed with our local guides — desert, mountain, culture and cuisine.",
          fr: "Excursions sur mesure conçues avec nos guides locaux — désert, montagne, culture et gastronomie.",
          es: "Excursiones a medida diseñadas con nuestros guías locales — desierto, montaña, cultura y cocina.",
        }, lang)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {d.excursions.map((e, i) => {
            const discoverLabel = pick({
              en: "Discover the excursion",
              fr: "Découvrir l'excursion",
              es: "Descubrir la excursión",
            }, lang);
            return (
              <Link
                key={`excursion-${e.title?.en || i}`}
                to={`/hotels/${hotel.slug}/excursions/${slugifyActivity(e.title) || i}`}
                className="group flex gap-5 hover:bg-[#F2EBE1]/40 -mx-4 px-4 py-3 transition-colors"
                data-testid={`excursion-${i}`}
              >
                <EditableImage
                  slot={`hotel.${hotel.slug}.excursions.${i}.thumb`}
                  fallback={pickThumb("excursions", i, e.image)}
                  alt={pick(e.title, lang)}
                  aspectRatio="4/3"
                  wrapperClassName="w-32 sm:w-40 shrink-0 self-start"
                  className="block w-full"
                  imgClassName="w-full h-full object-cover"
                />
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-1">
                    <span className="font-serif-x text-2xl text-[#C16542] leading-none">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-serif-x text-2xl md:text-3xl tracking-tight leading-tight group-hover:text-[#C16542] transition-colors">
                    {pick(e.title, lang)}
                  </h3>
                  <div className="mt-2 flex flex-wrap gap-4 text-xs tracking-[0.2em] uppercase text-[#5C5248]">
                    <span>{t("common.duration")} · {pick(e.duration, lang)}</span>
                    <span className="text-[#C16542]">{pick(e.price, lang)}</span>
                  </div>
                  <p className="mt-3 text-base text-[#5C5248] leading-relaxed">
                    {pick(e.desc, lang)}
                  </p>
                  <span
                    className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#2C2621] group-hover:text-[#C16542] border-b border-[#2C2621]/30 group-hover:border-[#C16542] pb-1 transition-colors"
                    data-testid={`excursion-${i}-cta`}
                  >
                    {discoverLabel}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* DESTINATION GUIDE */}
      <Section
        id="destination"
        overline={t("hotel.destination")}
        title={t("hotel.destination")}
        intro={d.destination_intro ? pick(d.destination_intro, lang) : pick({
          en: "A territory of contrasts within an hour's drive — from millennial markets to UNESCO ksour.",
          fr: "Un territoire de contrastes à moins d'une heure — marchés millénaires, ksour classés UNESCO.",
          es: "Un territorio de contrastes a menos de una hora — mercados milenarios, ksour UNESCO.",
        }, lang)}
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {d.destination.map((p, i) => {
            const SPANS = ["md:col-span-7", "md:col-span-5"];
            const span = SPANS[i] || "md:col-span-4";
            const discoverLabel = pick({
              en: "Discover this place",
              fr: "Découvrir ce lieu",
              es: "Descubrir este lugar",
            }, lang);
            return (
              <Link
                key={`nearby-${p.title?.en || i}`}
                to={`/hotels/${hotel.slug}/nearby/${slugifyActivity(p.title) || i}`}
                className={`${span} group bg-[#F2EBE1]/50 border border-[#2C2621]/10 hover:border-[#C16542]/40 hover:bg-[#F2EBE1] flex flex-col transition-colors overflow-hidden`}
                data-testid={`destination-${i}`}
              >
                <EditableImage
                  slot={`hotel.${hotel.slug}.destination.${i}.thumb`}
                  fallback={pickThumb("destination", i, p.image)}
                  alt={pick(p.title, lang)}
                  aspectRatio="16/9"
                  wrapperClassName="block w-full"
                  className="block w-full"
                  imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#C16542]">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {pick(p.distance, lang)}
                  </div>
                  <h3 className="font-serif-x text-2xl md:text-3xl mt-3 leading-tight group-hover:text-[#C16542] transition-colors">
                    {pick(p.title, lang)}
                  </h3>
                  <p className="mt-3 text-base text-[#5C5248] leading-relaxed">
                    {pick(p.desc, lang)}
                  </p>
                  <span
                    className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#2C2621] group-hover:text-[#C16542] border-b border-[#2C2621]/30 group-hover:border-[#C16542] pb-1 w-fit transition-colors"
                    data-testid={`destination-${i}-cta`}
                  >
                    {discoverLabel}
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* SPA MENU — hidden on bivouacs (hotel-only spa treatment list) */}
      {!bivouac && (
      <Section
        id="spa"
        overline={t("hotel.spa")}
        title={t("hotel.spa")}
        intro={pick({
          en: "A treatment menu inspired by Berber traditions, refined for our spa cabins.",
          fr: "Une carte de soins inspirée des traditions berbères, raffinée pour nos cabines de spa.",
          es: "Una carta de tratamientos inspirada en las tradiciones bereberes, refinada para nuestras cabinas.",
        }, lang)}
        dark
      >
        <div className="space-y-px bg-[#FDFBF7]/10">
          {d.spa_menu.map((s, i) => (
            <div
              key={`spa-${s.title?.en || i}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 px-2 bg-[#1A1513] hover:bg-[#1A1513]/70 transition-colors"
              data-testid={`spa-item-${i}`}
            >
              <div className="md:col-span-6">
                <h3 className="font-serif-x text-2xl md:text-3xl text-[#FDFBF7] leading-tight">
                  {pick(s.title, lang)}
                </h3>
                <p className="mt-2 text-sm text-[#FDFBF7]/70 leading-relaxed">
                  {pick(s.desc, lang)}
                </p>
              </div>
              <div className="md:col-span-3 text-sm text-[#D4A373] tracking-[0.2em] uppercase md:text-right self-start md:self-center">
                {s.duration}
              </div>
              <div className="md:col-span-3 font-serif-x text-2xl text-[#FDFBF7] md:text-right self-start md:self-center">
                {parsePrice(s.price) || s.price}
              </div>
            </div>
          ))}
        </div>
      </Section>
      )}

      {/* BEAUTY — hidden on bivouacs (hotel-only beauty rituals) */}
      {!bivouac && (
      <Section
        id="beauty"
        overline={t("hotel.beauty")}
        title={t("hotel.beauty")}
        intro={pick({
          en: "Traditional Moroccan beauty rituals — henna, argan, ghassoul — performed by our in-house artists.",
          fr: "Rituels de beauté marocains — henné, argan, ghassoul — réalisés par nos artisans maison.",
          es: "Rituales de belleza marroquíes — henna, argán, ghassoul — realizados por nuestras artistas.",
        }, lang)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
          {d.beauty.map((b, i) => (
            <article key={`beauty-${b.title?.en || i}`} className="flex justify-between gap-6 border-b border-[#2C2621]/10 pb-6" data-testid={`beauty-${i}`}>
              <div className="flex-1">
                <h3 className="font-serif-x text-2xl leading-tight">{pick(b.title, lang)}</h3>
                <p className="mt-2 text-sm text-[#5C5248] leading-relaxed">{pick(b.desc, lang)}</p>
              </div>
              <div className="font-serif-x text-2xl text-[#C16542] whitespace-nowrap">{parsePrice(b.price) || b.price}</div>
            </article>
          ))}
        </div>
      </Section>
      )}

      {/* FOOD & BEVERAGE */}
      <Section
        id="food"
        overline={t("hotel.food")}
        title={t("hotel.food")}
        intro={pick(
          bivouac
            ? {
                en: "One long communal table around the fire — a chef's tasting menu of Sahrawi cuisine, mint tea poured high and cocktails at the ember circle.",
                fr: "Une longue table commune autour du feu — menu dégustation du chef de cuisine sahraouie, thé à la menthe versé haut et cocktails au cercle de braises.",
                es: "Una única mesa larga en torno al fuego — menú degustación del chef de cocina saharaui, té con menta servido en alto y cócteles junto al círculo de brasas.",
              }
            : {
                en: "Two restaurants, a rooftop bar and a library lounge — a tour of Moroccan and Mediterranean tables.",
                fr: "Deux restaurants, un bar rooftop et un lounge bibliothèque — un tour de tables marocaines et méditerranéennes.",
                es: "Dos restaurantes, un bar en la azotea y un lounge biblioteca — un recorrido por mesas marroquíes y mediterráneas.",
              },
          lang
        )}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {d.fb.restaurants.map((r, i) => (
            <div
              key={`restaurant-${r.name || i}`}
              className="bg-[#F2EBE1]/50 border border-[#2C2621]/10 overflow-hidden"
              data-testid={`restaurant-${i}`}
            >
              <EditableImage
                slot={`hotel.${hotel.slug}.restaurants.${i}.thumb`}
                fallback={pickThumb("restaurants", i, r.image)}
                alt={r.name || ""}
                aspectRatio="16/9"
                wrapperClassName="block w-full"
                className="block w-full"
                imgClassName="w-full h-full object-cover"
              />
              <div className="p-8">
                <p className="overline">{t("hotel.restaurants")}</p>
                <h3 className="font-serif-x text-3xl mt-3 leading-tight">{r.name}</h3>
                <p className="text-sm tracking-wider uppercase text-[#C16542] mt-2">{pick(r.style, lang)}</p>
                <p className="mt-4 text-base text-[#5C5248] leading-relaxed">{pick(r.desc, lang)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="overline">{t("hotel.signature_drinks")}</p>
            <div className="mt-6 space-y-5">
              {d.fb.signature_drinks.map((dr, i) => (
                <div
                  key={`drink-${dr.name?.en || i}`}
                  className="flex justify-between items-baseline gap-6 border-b border-[#2C2621]/10 pb-4"
                  data-testid={`drink-${i}`}
                >
                  <div>
                    <div className="font-serif-x text-xl">{pick(dr.name, lang)}</div>
                    <div className="text-xs text-[#5C5248] mt-1">{dr.ingredients}</div>
                  </div>
                  <div className="font-serif-x text-xl text-[#C16542]">{parsePrice(dr.price) || dr.price}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="overline">{t("hotel.wines")}</p>
            <div className="mt-6 space-y-5">
              {d.fb.wines.map((w, i) => (
                <div key={`wine-${w.region?.en || i}`} className="border-b border-[#2C2621]/10 pb-4" data-testid={`wine-${i}`}>
                  <div className="font-serif-x text-xl">{pick(w.region, lang)}</div>
                  <div className="text-sm text-[#5C5248] mt-1">{w.notes}</div>
                </div>
              ))}
            </div>
            <p className="overline mt-10">{t("hotel.bars")}</p>
            <div className="mt-6 space-y-5">
              {d.fb.bars.map((b, i) => (
                <div key={`bar-${b.name?.en || i}`} data-testid={`bar-${i}`}>
                  <div className="font-serif-x text-xl">{pick(b.name, lang)}</div>
                  <div className="text-sm text-[#5C5248] mt-1">{pick(b.desc, lang)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* EVENTS */}
      <Section
        id="events"
        overline={t("sections.events_overline")}
        title={t("hotel.events")}
        intro={pick({
          en: "Bespoke event production from intimate ceremonies to thousand-guest gatherings.",
          fr: "Production événementielle sur mesure, des cérémonies intimes aux rassemblements de mille invités.",
          es: "Producción de eventos a medida, de ceremonias íntimas a celebraciones de mil invitados.",
        }, lang)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {d.events.map((e, i) => (
            <article key={`event-${e.title?.en || i}`} className="flex gap-5" data-testid={`event-${i}`}>
              <EditableImage
                slot={`hotel.${hotel.slug}.events.${i}.thumb`}
                fallback={pickThumb("events", i, e.image)}
                alt={pick(e.title, lang)}
                aspectRatio="4/3"
                wrapperClassName="w-32 sm:w-40 shrink-0 self-start"
                className="block w-full"
                imgClassName="w-full h-full object-cover"
              />
              <div className="flex-1">
                <span className="font-serif-x text-2xl text-[#C16542] leading-none block mb-2">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif-x text-2xl md:text-3xl tracking-tight leading-tight">
                  {pick(e.title, lang)}
                </h3>
                <p className="mt-3 text-base text-[#5C5248] leading-relaxed">
                  {pick(e.desc, lang)}
                </p>
              </div>
            </article>
          ))}
        </div>
        <div className="mt-12">
          <Link
            to="/contact"
            className="inline-flex bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.2em] uppercase"
            data-testid="event-rfp-button"
          >
            {t("hotel.group_request")}
          </Link>
        </div>
      </Section>

      {/* REVIEWS */}
      <section className="py-20 md:py-28 bg-[#F2EBE1]/50" data-testid="section-reviews">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="overline">{t("hotel.reviews")}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
              {t("hotel.reviews")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {d.reviews.map((r, i) => (
              <figure
                key={`review-${r.author || i}`}
                className="bg-[#FDFBF7] border border-[#2C2621]/10 p-8 flex flex-col"
                data-testid={`review-${i}`}
              >
                <Quote className="w-8 h-8 text-[#C16542]" strokeWidth={1.5} />
                <div className="mt-4 flex items-center gap-1 text-[#C16542]">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={`star-${r.name || ""}-${k}`} className="w-4 h-4 fill-[#C16542]" strokeWidth={1.5} />
                  ))}
                </div>
                <blockquote className="mt-4 font-serif-x text-lg md:text-xl italic text-[#2C2621] leading-snug flex-1">
                  “{pick(r.text, lang)}”
                </blockquote>
                <figcaption className="mt-6 text-sm">
                  <div className="font-semibold text-[#2C2621]">{r.name}</div>
                  <div className="text-[#5C5248] text-xs tracking-wider uppercase mt-1">
                    {r.source}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
          <div className="mt-10 text-center">
            <a
              href="https://www.tripadvisor.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-block border-b border-[#2C2621]/30 pb-1 text-xs tracking-[0.25em] uppercase hover:border-[#C16542] hover:text-[#C16542] transition-colors"
              data-testid="reviews-cta"
            >
              {t("hotel.reviews_cta")} →
            </a>
          </div>
        </div>
      </section>

      {/* LOCATION & CONTACT — map, directions, contact, hours */}
      <HotelLocationContact hotel={hotel} />

      {/* DID YOU KNOW? — horizontal postcard slider of property curiosities */}
      <DidYouKnowSection slug={hotel.slug} propertyName={hotel.name} />

      {/* Berber motif — symbolic transition into the booking journey */}
      <div className="bg-[#FDFBF7] py-10 md:py-12">
        <BerberDiamondDivider tone="umber" className="max-w-lg mx-auto px-6 opacity-80" />
      </div>

      {/* HOW TO BOOK — premium booking journey, consistent across all hotels & bivouacs */}
      <HowToBookSection hotel={hotel} />

      {/* BOOKING CTA */}
      <section id="contact" className="py-24 md:py-32 bg-[#1A1513] text-[#FDFBF7]" data-testid="section-booking-cta">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <p className="overline !text-[#D4A373]">{t("hotel.book_section")}</p>
            <h2 className="font-serif-x mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {t("hotel.book_section")} — {hotel.name}
            </h2>
            <p className="mt-6 text-lg text-[#FDFBF7]/80 leading-relaxed max-w-xl">
              {t("hotel.booking_intro")}
            </p>
            <Link
              to={`/booking?hotel=${encodeURIComponent(hotel.slug)}`}
              className="mt-10 inline-flex bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-10 py-4 text-xs tracking-[0.2em] uppercase"
              data-testid="hotel-final-book"
            >
              {t("common.book_now")} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-5 md:col-start-8 border-t md:border-t-0 md:border-l border-[#FDFBF7]/15 md:pl-10 pt-8 md:pt-0">
            <p className="overline !text-[#D4A373]">{t("hotel.contact")}</p>
            <ul className="mt-6 flex flex-col gap-4 text-sm">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
                <a href={`tel:${d.contact.phone}`} className="hover:text-[#D4A373]" data-testid="hotel-phone">
                  {d.contact.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
                <a href={`mailto:${d.contact.email}`} className="hover:text-[#D4A373]" data-testid="hotel-email">
                  {d.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[#D4A373]" strokeWidth={1.5} />
                <a
                  href={`https://wa.me/${d.contact.whatsapp.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#D4A373]"
                  data-testid="hotel-whatsapp"
                >
                  WhatsApp · {d.contact.whatsapp}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[#D4A373]" strokeWidth={1.5} />
                <span className="text-[#FDFBF7]/80">{hotel.location} · {d.stats.coords}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="py-24 md:py-32" data-testid="section-philosophy">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="overline">{t("hotel.philosophy")}</p>
          <blockquote className="mt-6 font-serif-x text-3xl md:text-5xl italic leading-[1.15] text-[#2C2621]">
            {pick(d.philosophy.quote, lang)}
          </blockquote>
          <p className="mt-10 text-lg text-[#5C5248] leading-relaxed max-w-2xl mx-auto">
            {pick(d.philosophy.text, lang)}
          </p>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="py-20 md:py-28 bg-[#F2EBE1]/50 border-t border-[#2C2621]/10" data-testid="section-ecosystem">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <p className="overline">{t("hotel.ecosystem")}</p>
              <h2 className="font-serif-x mt-4 text-4xl md:text-5xl tracking-tight leading-tight">
                {t("hotel.ecosystem")}
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-lg text-[#5C5248] leading-relaxed">{t("hotel.ecosystem_sub")}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/hotels/${o.slug}`}
                className="group relative aspect-[3/4] overflow-hidden"
                data-testid={`ecosystem-link-${o.slug}`}
                data-cms-scope={`hotel-ecosystem.${o.slug}`}
              >
                <img
                  src={o.image}
                  alt={o.name}
                  data-cms-slot={`cms.hotel.${o.slug}.thumb.0`}
                  data-cms-role="card.image"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/85 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end text-[#FDFBF7]">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#D4A373]">{o.location}</div>
                  <div className="font-serif-x text-lg md:text-xl mt-1 leading-tight">{o.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ROOM GALLERY LIGHTBOX */}
      {galleryRoom && (
        <RoomLightbox
          images={getRoomImagesFromAdmin(galleryRoom, hotel, adminGalleries)}
          title={pick(galleryRoom.name, lang)}
          onClose={() => setGalleryRoom(null)}
          lang={lang}
        />
      )}
    </div>
  );
};

export default HotelDetailPage;
